/**
 * PassportPage - "Mi Pasaporte" digital
 * Renderiza un pasaporte interactivo con el mismo estilo visual
 * de las guías (formato vertical con navegación prev/next).
 *
 * Paginación de sellos: máx 12 sellos por página de índice.
 * Si hay más de 12 se generan páginas de índice adicionales.
 */

import React, {
	useState,
	useEffect,
	useRef,
	useCallback,
	useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	CircularProgress,
	Typography,
	IconButton,
	Tooltip,
	Snackbar,
	Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import IosShareIcon from "@mui/icons-material/IosShare";
import { useFirebase } from "../context/ApiContext";
import Auth from "../components/Auth/Auth";
import { BasicModal } from "../components/BasicModal/BasicModal";
import PassportCover from "../components/Passport/PassportCover";
import PassportIndex from "../components/Passport/PassportIndex";
import PassportCountryPage from "../components/Passport/PassportCountryPage";
import PassportBackCover from "../components/Passport/PassportBackCover";
import AddCountryDialog from "../components/Passport/AddCountryDialog";
import SharePassportModal from "../components/Passport/SharePassportModal";
import { trackPassportAction } from "../utils/analytics";
import {
	DEMO_PASSPORT,
	DEMO_PASSPORT_COUNTRIES,
} from "../data/demoPassport";

const STAMPS_PER_PAGE = 12;

const PassportPage = () => {
	const navigate = useNavigate();
	const {
		user,
		getPassport,
		updatePassport,
		addPassportCountry,
		updatePassportCountry,
		deletePassportCountry,
		addPassportPhoto,
		deletePassportPhoto,
		sharePassport,
	} = useFirebase();

	const [loading, setLoading] = useState(true);
	const [passport, setPassport] = useState(null);
	const [countries, setCountries] = useState([]);
	const [currentPageIndex, setCurrentPageIndex] = useState(0);
	const [direction, setDirection] = useState("next");
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	// Share modal state
	const [shareDialogOpen, setShareDialogOpen] = useState(false);
	const [showLogin, setShowLogin] = useState(false);
	const [authMode, setAuthMode] = useState("signup");
	const isDemo = !user;

	const touchStartX = useRef(0);
	const touchStartY = useRef(0);

	// Bloquear scroll del body en iOS Safari (evitar doble scroll y barras de navegación raras)
	useEffect(() => {
		document.documentElement.classList.add("passport-page-active");
		document.body.classList.add("passport-page-active");
		return () => {
			document.documentElement.classList.remove("passport-page-active");
			document.body.classList.remove("passport-page-active");
		};
	}, []);

	const requireAuth = (message) => {
		if (user) return false;
		setAuthMode("signup");
		setShowLogin(true);
		setSnackbar({
			open: true,
			message:
				message || "Crea una cuenta para guardar tus viajes.",
			severity: "info",
		});
		return true;
	};

	// Cargar pasaporte (demo si no hay sesión)
	useEffect(() => {
		const loadPassport = async () => {
			if (!user) {
				setPassport(DEMO_PASSPORT);
				setCountries(DEMO_PASSPORT_COUNTRIES);
				setLoading(false);
				return;
			}
			try {
				const data = await getPassport();
				setPassport(data);
				setCountries(data.countries || []);
			} catch (error) {
				console.error("Error cargando pasaporte:", error);
			} finally {
				setLoading(false);
			}
		};
		loadPassport();
	}, [user]);

	// Agrupar stamps por país (countryCode): un sello y una página por país.
	// Normalizar código (trim + mayúsculas) y ordenar por "order" para evitar duplicados y orden estable.
	const uniqueCountries = useMemo(() => {
		const byCode = {};
		(countries || []).forEach((stamp) => {
			const code = (stamp.countryCode || "").trim().toUpperCase();
			if (!code) return;
			if (!byCode[code]) {
				byCode[code] = {
					countryCode: stamp.countryCode?.trim() || stamp.countryCode,
					countryName: stamp.countryName,
					visits: [],
				};
			}
			byCode[code].visits.push(stamp);
		});
		const groups = Object.values(byCode);
		groups.forEach((g) => {
			g.visits.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
		});
		groups.sort((a, b) => {
			const orderA = Math.min(...a.visits.map((v) => v.order ?? 999999));
			const orderB = Math.min(...b.visits.map((v) => v.order ?? 999999));
			return orderA - orderB;
		});
		return groups;
	}, [countries]);

	const numUniqueCountries = uniqueCountries.length;

	// Nombre en portada: mismo criterio que el backend al crear/sincronizar el pasaporte
	const profileHolderName = useMemo(() => {
		if (!user) return DEMO_PASSPORT.displayName;
		return (
			user.displayName?.trim() ||
			user.email?.split("@")[0] ||
			undefined
		);
	}, [user]);

	// ---------- Paginación ----------
	// Páginas de índice: ceil(uniqueCountries / 12), mínimo 1
	const indexPageCount = useMemo(
		() => Math.max(1, Math.ceil(numUniqueCountries / STAMPS_PER_PAGE)),
		[numUniqueCountries]
	);

	// Si la última página de índice tiene 12 sellos, añadimos una página extra solo con el botón "Añadir"
	const needAddOnlyPage = useMemo(
		() => numUniqueCountries > 0 && numUniqueCountries % STAMPS_PER_PAGE === 0,
		[numUniqueCountries]
	);
	const totalIndexPages = indexPageCount + (needAddOnlyPage ? 1 : 0);

	// Total: 1 portada + N índices (+ 1 solo añadir si aplica) + M países (únicos) + 1 contraportada
	const totalPages = 1 + totalIndexPages + numUniqueCountries + 1;

	// Offsets rápidos
	const firstIndexPage = 1;
	const firstCountryPage = 1 + totalIndexPages;
	const backCoverPage = totalPages - 1;

	const handlePrev = useCallback(() => {
		setDirection("prev");
		setCurrentPageIndex((prev) => Math.max(0, prev - 1));
	}, []);

	const handleNext = useCallback(() => {
		setDirection("next");
		setCurrentPageIndex((prev) => Math.min(totalPages - 1, prev + 1));
	}, [totalPages]);

	// Swipe gestures
	const handleTouchStart = (e) => {
		touchStartX.current = e.touches[0].clientX;
		touchStartY.current = e.touches[0].clientY;
	};

	const handleTouchEnd = (e) => {
		const deltaX = e.changedTouches[0].clientX - touchStartX.current;
		const deltaY = e.changedTouches[0].clientY - touchStartY.current;
		if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
			if (deltaX < 0) handleNext();
			else handlePrev();
		}
	};

	// Keyboard navigation
	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === "ArrowLeft") handlePrev();
			if (e.key === "ArrowRight") handleNext();
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [handlePrev, handleNext]);

	// ---------- Callbacks ----------

	// Añadir país — NO navega a la página del país
	const handleAddCountry = async (countryData) => {
		if (requireAuth("Crea una cuenta para añadir tus sellos.")) return;
		try {
			const newCountry = await addPassportCountry(countryData);
			trackPassportAction("country_added", {
				country_code: countryData.countryCode,
				country_name: countryData.countryName,
			});
			const updatedCountries = [...countries, newCountry];
			setCountries(updatedCountries);
			setAddDialogOpen(false);
			setSnackbar({
				open: true,
				message: "Sello añadido a tu pasaporte",
				severity: "success",
			});

			// Ir a la última página de índice (por países únicos)
			const newByCode = {};
			updatedCountries.forEach((s) => {
				const c = (s.countryCode || "").toUpperCase();
				if (!newByCode[c]) newByCode[c] = true;
			});
			const newNumUnique = Object.keys(newByCode).length;
			const newIndexPageCount = Math.max(
				1,
				Math.ceil(newNumUnique / STAMPS_PER_PAGE)
			);
			const newNeedAddOnly =
				newNumUnique > 0 && newNumUnique % STAMPS_PER_PAGE === 0;
			const newTotalIndexPages = newIndexPageCount + (newNeedAddOnly ? 1 : 0);
			setDirection("next");
			setCurrentPageIndex(firstIndexPage + newTotalIndexPages - 1);
		} catch (error) {
			setSnackbar({
				open: true,
				message: "Error al añadir el país",
				severity: "error",
			});
		}
	};

	// Actualizar país
	const handleUpdateCountry = async (countryId, data) => {
		if (requireAuth()) return;
		try {
			const updated = await updatePassportCountry(countryId, data);
			setCountries((prev) =>
				prev.map((c) => (c.id === countryId ? { ...c, ...updated } : c))
			);
		} catch (error) {
			setSnackbar({
				open: true,
				message: "Error al actualizar",
				severity: "error",
			});
		}
	};

	// Eliminar país (puede ser un id o array de ids para borrar todas las visitas del país)
	const handleDeleteCountry = async (idOrIds) => {
		if (requireAuth()) return;
		const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
		trackPassportAction("country_deleted");
		try {
			for (const id of ids) {
				await deletePassportCountry(id);
			}
			setCountries((prev) => prev.filter((c) => !ids.includes(c.id)));
			setDirection("prev");
			setCurrentPageIndex((prev) => Math.max(0, prev - 1));
			setSnackbar({
				open: true,
				message: "País eliminado del pasaporte",
				severity: "info",
			});
		} catch (error) {
			setSnackbar({
				open: true,
				message: "Error al eliminar",
				severity: "error",
			});
		}
	};

	// Añadir otra ciudad/visita al mismo país (desde la página del país)
	const handleAddVisit = async (countryCode, countryName, data) => {
		if (requireAuth()) return;
		trackPassportAction("visit_added", { country_code: countryCode, country_name: countryName });
		try {
			const newVisit = await addPassportCountry({
				countryCode,
				countryName,
				city: data?.city ?? null,
				visitDate: data?.visitDate ?? null,
				notes: data?.notes ?? null,
			});
			setCountries((prev) => [...prev, newVisit]);
			setSnackbar({
				open: true,
				message: "Ciudad añadida",
				severity: "success",
			});
		} catch (error) {
			setSnackbar({
				open: true,
				message: "Error al añadir ciudad",
				severity: "error",
			});
		}
	};

	// Eliminar una ciudad/visita; si era la última del país, volver al índice
	const handleDeleteVisit = async (visitId) => {
		if (requireAuth()) return;
		try {
			await deletePassportCountry(visitId);
			setCountries((prev) => {
				const next = prev.filter((c) => c.id !== visitId);
				const remainingForCurrent = next.filter(
					(c) =>
						(c.countryCode || "").toUpperCase() ===
						(
							uniqueCountries[currentPageIndex - firstCountryPage]
								?.countryCode || ""
						).toUpperCase()
				);
				if (remainingForCurrent.length === 0) {
					setDirection("prev");
					setCurrentPageIndex((prev) =>
						Math.max(0, firstCountryPage - 1)
					);
				}
				return next;
			});
			setSnackbar({
				open: true,
				message: "Ciudad eliminada",
				severity: "info",
			});
		} catch (error) {
			setSnackbar({
				open: true,
				message: "Error al eliminar",
				severity: "error",
			});
		}
	};

	// Añadir foto (guarda en backend y actualiza estado)
	const handleAddPhoto = async (countryId, photoData) => {
		if (requireAuth("Crea una cuenta para guardar tus fotos.")) return;
		trackPassportAction("photo_added");
		try {
			const res = await addPassportPhoto(countryId, photoData);
			// La API devuelve { id, url, caption, createdAt }
			const newPhoto =
				res && typeof res === "object" && res.url != null
					? {
							id: res.id,
							url: res.url,
							caption: res.caption || "",
							createdAt: res.createdAt,
					  }
					: {
							id: res?.id || `photo-${Date.now()}`,
							url: photoData?.photo || "",
							caption: photoData?.caption || "",
							createdAt: res?.createdAt || Date.now(),
					  };
			setCountries((prev) =>
				prev.map((c) =>
					String(c.id) === String(countryId)
						? { ...c, photos: [...(c.photos || []), newPhoto] }
						: c
				)
			);
			setSnackbar({ open: true, message: "Foto añadida", severity: "success" });
		} catch (error) {
			setSnackbar({
				open: true,
				message: "Error al añadir foto",
				severity: "error",
			});
		}
	};

	// Eliminar foto
	const handleDeletePhoto = async (countryId, photoId) => {
		if (requireAuth()) return;
		try {
			await deletePassportPhoto(countryId, photoId);
			setCountries((prev) =>
				prev.map((c) =>
					String(c.id) === String(countryId)
						? {
								...c,
								photos: (c.photos || []).filter((p) => p && p.id !== photoId),
						  }
						: c
				)
			);
		} catch (error) {
			setSnackbar({
				open: true,
				message: "Error al eliminar foto",
				severity: "error",
			});
		}
	};

	// Navegar a la página de un país desde un índice
	const handleNavigateToCountry = (globalCountryIndex) => {
		setDirection("next");
		setCurrentPageIndex(firstCountryPage + globalCountryIndex);
	};

	// ---------- Renders ----------

	if (loading) {
		return (
			<Box
				sx={{
					position: "fixed",
					inset: 0,
					height: "100%",
					maxHeight: "-webkit-fill-available",
					overflow: "hidden",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: "#f7f7f7",
				}}
			>
				<Box sx={{ textAlign: "center" }}>
					<CircularProgress sx={{ color: "#8b6914" }} size={48} />
					<Typography
						sx={{
							mt: 2,
							color: "#5c4a32",
							fontFamily: "'Playfair Display', serif",
						}}
					>
						Abriendo tu pasaporte...
					</Typography>
				</Box>
			</Box>
		);
	}

	// Renderizar la página actual
	const renderCurrentPage = () => {
		// Portada
		if (currentPageIndex === 0) {
			return (
				<PassportCover
					passport={passport}
					countriesCount={countries.length}
					profileHolderName={profileHolderName}
				/>
			);
		}

		// Contraportada
		if (currentPageIndex === backCoverPage) {
			return <PassportBackCover countriesCount={countries.length} />;
		}

		// Páginas de índice (1 .. totalIndexPages); un sello por país
		if (
			currentPageIndex >= firstIndexPage &&
			currentPageIndex < firstCountryPage
		) {
			const idxPage = currentPageIndex - firstIndexPage; // 0-based
			const isAddOnlyPage = needAddOnlyPage && idxPage === indexPageCount;
			const start = idxPage * STAMPS_PER_PAGE;
			const end = Math.min(start + STAMPS_PER_PAGE, numUniqueCountries);
			const slice = isAddOnlyPage ? [] : uniqueCountries.slice(start, end);

			return (
				<PassportIndex
					countries={slice}
					globalOffset={isAddOnlyPage ? numUniqueCountries : start}
					totalCountries={numUniqueCountries}
					currentIndexPage={idxPage}
					totalIndexPages={totalIndexPages}
					isLastIndexPage={idxPage === totalIndexPages - 1}
					onNavigate={handleNavigateToCountry}
					onAddCountry={() => {
						if (requireAuth("Crea una cuenta para añadir tus sellos.")) return;
						setAddDialogOpen(true);
					}}
				/>
			);
		}

		// Páginas de país (una por país único; dentro, sección con ciudades si hay)
		const countryIndex = currentPageIndex - firstCountryPage;
		const group = uniqueCountries[countryIndex];
		if (!group) return null;
		const firstVisit = group.visits[0];
		const visitIds = group.visits.map((v) => v.id);

		return (
			<PassportCountryPage
				country={firstVisit}
				visits={group.visits}
				pageNumber={currentPageIndex}
				onUpdate={handleUpdateCountry}
				onDelete={() => handleDeleteCountry(visitIds)}
				onDeleteVisit={handleDeleteVisit}
				onAddVisit={handleAddVisit}
				onAddPhoto={(photoData) => handleAddPhoto(firstVisit.id, photoData)}
				onDeletePhoto={(photoId) => handleDeletePhoto(firstVisit.id, photoId)}
			/>
		);
	};

	return (
		<Box
			sx={{
				position: "fixed",
				inset: 0,
				height: "100%",
				maxHeight: "-webkit-fill-available",
				overflow: "hidden",
				background: "#f7f7f7",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				zIndex: 0,
			}}
		>
			{/* Patrón de fondo sutil (como en las guías) */}
			<Box
				sx={{
					position: "fixed",
					inset: 0,
					backgroundImage: `
						radial-gradient(circle at 20% 20%, rgba(0, 0, 0, 0.02) 0%, transparent 40%),
						radial-gradient(circle at 80% 80%, rgba(0, 0, 0, 0.015) 0%, transparent 40%)
					`,
					pointerEvents: "none",
					zIndex: 0,
				}}
			/>

			{isDemo && (
				<Box
					onClick={() => requireAuth("Crea una cuenta para guardar tus viajes.")}
					sx={{
						position: "fixed",
						top: 68,
						left: "50%",
						transform: "translateX(-50%)",
						zIndex: 100,
						px: 2,
						py: 0.7,
						borderRadius: 999,
						backgroundColor: "rgba(139, 105, 20, 0.92)",
						color: "#fff",
						fontSize: "0.78rem",
						fontWeight: 600,
						cursor: "pointer",
						whiteSpace: "nowrap",
						boxShadow: "0 8px 20px rgba(92, 74, 50, 0.25)",
						"&:hover": { backgroundColor: "rgba(139, 105, 20, 1)" },
					}}
				>
					Ejemplo · Crea una cuenta para el tuyo
				</Box>
			)}

			{/* Botón volver */}
			<Box sx={{ position: "fixed", top: 16, left: 16, zIndex: 100 }}>
				<Tooltip title="Volver al Dashboard">
					<IconButton
						onClick={() => navigate("/")}
						sx={{
							color: "#5c4a32",
							backgroundColor: "rgba(92, 74, 50, 0.08)",
							border: "1px solid rgba(92, 74, 50, 0.15)",
							"&:hover": { backgroundColor: "rgba(92, 74, 50, 0.12)" },
						}}
					>
						<ArrowBackIosNewIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>

			{/* Contador de páginas + Compartir */}
			<Box
				sx={{
					position: "fixed",
					top: 16,
					right: 16,
					zIndex: 100,
					display: "flex",
					alignItems: "center",
					gap: 1,
				}}
			>
				<Typography
					sx={{
						color: "#5c4a32",
						fontSize: "0.85rem",
						fontFamily: "'Playfair Display', serif",
						letterSpacing: "0.05em",
					}}
				>
					{currentPageIndex + 1} / {totalPages}
				</Typography>
				<Tooltip title="Compartir pasaporte">
					<IconButton
						size="small"
						onClick={() => {
							if (requireAuth("Crea una cuenta para compartir tu pasaporte."))
								return;
							trackPassportAction("share_clicked");
							setShareDialogOpen(true);
						}}
						sx={{
							color: "#5c4a32",
							backgroundColor: "rgba(92, 74, 50, 0.08)",
							border: "1px solid rgba(92, 74, 50, 0.15)",
							"&:hover": { backgroundColor: "rgba(92, 74, 50, 0.12)" },
						}}
					>
						<IosShareIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</Tooltip>
			</Box>

			{/* Contenedor principal del pasaporte */}
			<Box
				onTouchStart={handleTouchStart}
				onTouchEnd={handleTouchEnd}
				sx={{
					width: "100%",
					maxWidth: 550,
					aspectRatio: "21 / 29.7",
					maxHeight: "calc(100% - 100px)",
					position: "relative",
					zIndex: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					mx: "auto",
					px: { xs: 2, sm: 3 },
				}}
			>
				<Box
					sx={{
						width: "100%",
						height: "100%",
						borderRadius: "12px",
						overflow: "hidden",
						boxShadow: "0 8px 24px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.06)",
						position: "relative",
						"@keyframes passportSlideNext": {
							from: { transform: "translateX(100%)", opacity: 0 },
							to: { transform: "translateX(0)", opacity: 1 },
						},
						"@keyframes passportSlidePrev": {
							from: { transform: "translateX(-100%)", opacity: 0 },
							to: { transform: "translateX(0)", opacity: 1 },
						},
					}}
				>
					<Box
						key={currentPageIndex}
						sx={{
							width: "100%",
							height: "100%",
							animation:
								direction === "next"
									? "passportSlideNext 0.4s ease-in-out"
									: "passportSlidePrev 0.4s ease-in-out",
						}}
					>
						{renderCurrentPage()}
					</Box>
				</Box>
			</Box>

			{/* Navegación */}
			<Box
				sx={{
					position: "fixed",
					bottom: 24,
					left: "50%",
					transform: "translateX(-50%)",
					display: "flex",
					alignItems: "center",
					gap: 3,
					zIndex: 100,
				}}
			>
				<IconButton
					onClick={handlePrev}
					disabled={currentPageIndex === 0}
					sx={{
						color: "#5c4a32",
						backgroundColor: "rgba(92, 74, 50, 0.08)",
						border: "1px solid rgba(92, 74, 50, 0.15)",
						width: 48,
						height: 48,
						"&:hover": { backgroundColor: "rgba(92, 74, 50, 0.12)" },
						"&.Mui-disabled": {
							color: "rgba(92, 74, 50, 0.25)",
							borderColor: "rgba(92, 74, 50, 0.08)",
						},
					}}
				>
					<ArrowBackIcon />
				</IconButton>

				{/* Dots de navegación */}
				<Box sx={{ display: "flex", gap: 0.75 }}>
					{Array.from({ length: Math.min(totalPages, 12) }).map((_, i) => {
						const dotIndex =
							totalPages <= 12
								? i
								: currentPageIndex <= 5
								? i
								: currentPageIndex >= totalPages - 6
								? totalPages - 12 + i
								: currentPageIndex - 5 + i;
						return (
							<Box
								key={dotIndex}
								onClick={() => {
									setDirection(dotIndex > currentPageIndex ? "next" : "prev");
									setCurrentPageIndex(dotIndex);
								}}
								sx={{
									width: dotIndex === currentPageIndex ? 24 : 8,
									height: 8,
									borderRadius: 4,
									backgroundColor:
										dotIndex === currentPageIndex
											? "#8b6914"
											: "rgba(92, 74, 50, 0.25)",
									cursor: "pointer",
									transition: "all 0.3s ease",
									"&:hover": {
										backgroundColor:
											dotIndex === currentPageIndex
												? "#8b6914"
												: "rgba(92, 74, 50, 0.4)",
									},
								}}
							/>
						);
					})}
				</Box>

				<IconButton
					onClick={handleNext}
					disabled={currentPageIndex === totalPages - 1}
					sx={{
						color: "#5c4a32",
						backgroundColor: "rgba(92, 74, 50, 0.08)",
						border: "1px solid rgba(92, 74, 50, 0.15)",
						width: 48,
						height: 48,
						"&:hover": { backgroundColor: "rgba(92, 74, 50, 0.12)" },
						"&.Mui-disabled": {
							color: "rgba(92, 74, 50, 0.25)",
							borderColor: "rgba(92, 74, 50, 0.08)",
						},
					}}
				>
					<ArrowForwardIcon />
				</IconButton>
			</Box>

			{/* Dialog para añadir país */}
			<AddCountryDialog
				open={addDialogOpen}
				onClose={() => setAddDialogOpen(false)}
				onAdd={handleAddCountry}
			/>

			{/* Snackbar */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={3000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
					severity={snackbar.severity}
					sx={{
						...(snackbar.severity === "success"
							? {
									backgroundColor: "#f5f3ff",
									color: "#4f46e5",
									border: "1px solid rgba(99, 102, 241, 0.25)",
									boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
									"& .MuiAlert-icon": { color: "#6366F1" },
							  }
							: {}),
						...(snackbar.severity === "error"
							? {
									backgroundColor: "#fef2f2",
									color: "#b91c1c",
									border: "1px solid rgba(185, 28, 28, 0.2)",
							  }
							: {}),
					}}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>

			{/* Modal de Compartir Pasaporte */}
			<SharePassportModal
				open={shareDialogOpen}
				onClose={() => setShareDialogOpen(false)}
				passport={passport}
				countries={countries}
				onSharePassport={sharePassport}
				onSnackbar={setSnackbar}
				onShareSuccess={async () => {
					try {
						const data = await getPassport();
						setPassport(data);
						setCountries(data?.countries || []);
					} catch (e) {
						console.error("Error refrescando pasaporte:", e);
					}
				}}
			/>

			<BasicModal
				width={500}
				showModal={showLogin}
				handleClose={() => setShowLogin(false)}
			>
				<Auth setShowLogin={setShowLogin} initialMode={authMode} />
			</BasicModal>
		</Box>
	);
};

export default PassportPage;
